/**
 * Event handlers that update stores based on events
 * These handlers subscribe to the event bus and maintain application state
 */

import { eventBus } from './EventBus.js';
import { useChatStore } from '@/stores/chatStore.js';
import { useAgentStore } from '@/stores/agentStore.js';
import { useApprovalStore } from '@/stores/approvalStore.js';
import { useTasksStore } from '@/stores/tasksStore.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { useNotificationStore } from '@/stores/notificationStore.js';
import { useEventLogStore } from '@/stores/eventLogStore.js';
import type { Message } from '@/types/message.js';
import type { ApiClient } from '@/lib/api.js';
import { createNoteInStorage } from '@/lib/notesStorage.js';
import {
  isToolConfirmationApproval,
  isCommandConfirmationApproval,
  isElicitationApproval,
  getElicitationMetadata,
  type ApprovalRequestEvent,
  type ApprovalResolvedEvent,
} from './types.js';

// Track current streaming messages per session
const streamingMessages = new Map<string, Message>();

/**
 * Extract schedule data from tool result
 *
 * Tool results can come in various formats depending on the backend serialization:
 *
 * 1. Direct format (legacy/simple):
 *    { message: "...", schedule: {...} }
 *    { message: "...", scheduleId: "..." }
 *
 * 2. Meta format (tool metadata):
 *    { meta: { schedule: {...} }, content: [...] }
 *
 * 3. Content array with JSON string:
 *    { content: ['{"message": "...", "schedule": {...}}'] }
 *
 * 4. Content array with text object (MCP/Anthropic format):
 *    { content: [{ type: 'text', text: '{"message": "...", "schedule": {...}}' }] }
 *
 * 5. Content array with direct object:
 *    { content: [{ message: "...", schedule: {...} }] }
 *
 * To extend for new formats:
 * - Add a new extraction path below following the same pattern
 * - Check for the format-specific structure first
 * - Extract and return { schedule, scheduleId, message } as appropriate
 * - Keep extraction order from most specific to most generic
 */
function extractScheduleFromResult(data: Record<string, unknown>): { schedule?: any; scheduleId?: string; message?: string } {
  // Direct format: { schedule: {...} }
  if (data.schedule) {
    return { schedule: data.schedule, message: data.message as string };
  }

  // Direct format: { scheduleId: '...' }
  if (data.scheduleId) {
    return { scheduleId: data.scheduleId as string, message: data.message as string };
  }

  // Check meta for schedule
  if (data.meta && typeof data.meta === 'object') {
    const meta = data.meta as Record<string, unknown>;
    if (meta.schedule) {
      return { schedule: meta.schedule, message: data.message as string };
    }
    if (meta.scheduleId) {
      return { scheduleId: meta.scheduleId as string, message: data.message as string };
    }
  }

  // Check content array
  if (Array.isArray(data.content) && data.content.length > 0) {
    const contentItem = data.content[0];

    // Content item might be a string
    if (typeof contentItem === 'string') {
      try {
        const parsed = JSON.parse(contentItem);
        if (parsed.schedule) {
          return { schedule: parsed.schedule, message: parsed.message };
        }
        if (parsed.scheduleId) {
          return { scheduleId: parsed.scheduleId, message: parsed.message };
        }
      } catch {
        // Not JSON, might contain schedule info in text
      }
    }

    // Content item might be { type: 'text', text: '...' }
    if (contentItem && typeof contentItem === 'object' && 'text' in contentItem) {
      const textContent = (contentItem as { text: string }).text;
      try {
        const parsed = JSON.parse(textContent);
        if (parsed.schedule) {
          return { schedule: parsed.schedule, message: parsed.message };
        }
        if (parsed.scheduleId) {
          return { scheduleId: parsed.scheduleId, message: parsed.message };
        }
      } catch {
        // Not JSON
      }
    }

    // Content item might directly contain schedule
    if (contentItem && typeof contentItem === 'object') {
      const item = contentItem as Record<string, unknown>;
      if (item.schedule) {
        return { schedule: item.schedule, message: item.message as string };
      }
      if (item.scheduleId) {
        return { scheduleId: item.scheduleId as string, message: item.message as string };
      }
    }
  }

  return {};
}

/**
 * Handle scheduler tool results - update scheduler store based on tool execution
 * Tool names use custom-- prefix from the tool provider: custom--create_schedule, etc.
 */
function handleSchedulerToolResult(toolName: string, result: unknown): void {
  // Only handle scheduler tools - strip any prefix (custom--, scheduler--, etc.)
  // Tool names might be: create_schedule, custom--create_schedule, scheduler--create_schedule
  const normalizedName = toolName
    .replace(/^custom--/, '')
    .replace(/^scheduler--/, '')
    .replace(/^dexto--/, '');

  console.log('[Handler] 📅 handleSchedulerToolResult called:', {
    toolName,
    normalizedName,
    resultType: typeof result,
    resultPreview: typeof result === 'string' ? result.substring(0, 200) : JSON.stringify(result)?.substring(0, 200),
  });

  if (!normalizedName.includes('schedule')) {
    console.log('[Handler] 📅 Skipping - not a schedule tool:', normalizedName);
    return;
  }

  console.log('[Handler] 📅 Scheduler tool result:', normalizedName, result);

  // Parse result if string
  let parsedResult = result;
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result);
    } catch {
      // Result might be a plain message string, not JSON
      console.log('[Handler] Scheduler tool result is not JSON:', result);
      return;
    }
  }

  if (!parsedResult || typeof parsedResult !== 'object') {
    console.log('[Handler] 📅 Result is not an object, skipping:', typeof parsedResult);
    return;
  }

  const data = parsedResult as Record<string, unknown>;
  console.log('[Handler] 📅 Parsed data keys:', Object.keys(data));

  // Extract schedule data from potentially nested structure
  const extracted = extractScheduleFromResult(data);
  console.log('[Handler] 📅 Extracted data:', extracted);

  const schedulerStore = useSchedulerStore.getState();
  console.log('[Handler] 📅 Current schedules count:', schedulerStore.schedules.size);

  switch (normalizedName) {
    case 'create_schedule': {
      const schedule = extracted.schedule;
      console.log('[Handler] 📅 create_schedule - schedule:', schedule);
      if (schedule) {
        console.log('[Handler] 📅 Adding schedule to store:', schedule.name);
        schedulerStore.addSchedule(schedule);
        console.log('[Handler] 📅 After add, schedules count:', useSchedulerStore.getState().schedules.size);

        // Show toast notification
        console.log('[Handler] 📅 Creating toast notification...');
        useNotificationStore.getState().addToast({
          title: 'Routine created',
          description: `"${schedule.name}" has been scheduled`,
          intent: 'success',
        });
        console.log('[Handler] 📅 Toast created, current toasts:', useNotificationStore.getState().toasts.length);

        // Add to activity log
        console.log('[Handler] 📅 Adding to activity log...');
        useEventLogStore.getState().addEvent({
          type: 'success',
          category: 'scheduler',
          title: 'Schedule created',
          description: schedule.name,
          metadata: { scheduleId: schedule.id, cron: schedule.cronExpression },
        });
        console.log('[Handler] 📅 Activity log event count:', useEventLogStore.getState().events.length);
      } else {
        console.log('[Handler] 📅 No schedule found in data - raw keys:', Object.keys(data));
        console.log('[Handler] 📅 Full data structure:', JSON.stringify(data, null, 2).substring(0, 500));
      }
      break;
    }

    case 'update_schedule': {
      const schedule = extracted.schedule;
      console.log('[Handler] 📅 update_schedule - schedule:', schedule);
      if (schedule) {
        console.log('[Handler] 📅 Updating schedule in store:', schedule.id);
        schedulerStore.updateSchedule(schedule.id, schedule);

        // Show toast notification
        useNotificationStore.getState().addToast({
          title: 'Routine updated',
          description: `"${schedule.name}" has been updated`,
          intent: 'info',
        });

        // Add to activity log
        useEventLogStore.getState().addEvent({
          type: 'info',
          category: 'scheduler',
          title: 'Schedule updated',
          description: schedule.name,
          metadata: { scheduleId: schedule.id, cron: schedule.cronExpression },
        });
      } else {
        console.log('[Handler] 📅 No schedule in data');
      }
      break;
    }

    case 'delete_schedule': {
      const scheduleId = extracted.scheduleId || (data.scheduleId as string);
      console.log('[Handler] 📅 delete_schedule - scheduleId:', scheduleId);
      if (scheduleId) {
        // Get schedule name before deletion for the notification
        const existingSchedule = schedulerStore.schedules.get(scheduleId);
        const scheduleName = existingSchedule?.name || 'Schedule';

        console.log('[Handler] 📅 Removing schedule from store:', scheduleId);
        schedulerStore.removeSchedule(scheduleId);
        console.log('[Handler] 📅 After remove, schedules count:', useSchedulerStore.getState().schedules.size);

        // Show toast notification
        useNotificationStore.getState().addToast({
          title: 'Routine deleted',
          description: `"${scheduleName}" has been removed`,
          intent: 'info',
        });

        // Add to activity log
        useEventLogStore.getState().addEvent({
          type: 'info',
          category: 'scheduler',
          title: 'Schedule deleted',
          description: scheduleName,
          metadata: { scheduleId },
        });
      } else {
        console.log('[Handler] 📅 No scheduleId in data');
      }
      break;
    }

    default:
      console.log('[Handler] 📅 Unhandled schedule tool:', normalizedName);
      break;
  }
}

// Track sessions that have had their titles generated
const titlesGenerated = new Set<string>();

// Track if handlers have been initialized to prevent duplicate subscriptions
let handlersInitialized = false;

// Counter for generating unique message IDs (prevents collision during fast history replay)
let messageIdCounter = 0;

// Store reference to API client for title generation
let apiClientRef: ApiClient | null = null;

/**
 * Initialize all event handlers
 * Call this once when the app starts
 */
export function initializeEventHandlers(apiClient?: ApiClient): void {
  if (handlersInitialized) {
    console.warn('[EventHandlers] Already initialized, skipping duplicate initialization');
    return;
  }

  console.log('[EventHandlers] Initializing event handlers');
  handlersInitialized = true;

  // Store API client reference if provided
  if (apiClient) {
    apiClientRef = apiClient;
  }

  // Connection status handler
  eventBus.on('connectionStatus', (event) => {
    if (event.type !== 'connectionStatus') return;
    console.log('[Handler] connectionStatus event:', { status: event.status });
    useAgentStore.getState().setConnectionStatus(event.status);
  });

  // Thinking handler
  eventBus.on('thinking', (event) => {
    if (event.type !== 'thinking') return;
    const sessionId = event.sessionId || 'default';

    useChatStore.getState().setProcessing(sessionId, true);
    useChatStore.getState().setError(sessionId, null);
    useAgentStore.getState().setAgentStatus('thinking', 'Agent is thinking...');
    useAgentStore.getState().setActiveContext(sessionId, null);
  });

  // Chunk handler - build up streaming message per session
  eventBus.on('chunk', (event) => {
    if (event.type !== 'chunk') return;
    const sessionId = event.sessionId || 'default';

    useAgentStore.getState().setAgentStatus('thinking', 'Generating response...');

    let currentMessage = streamingMessages.get(sessionId);

    if (!currentMessage) {
      // Start new assistant message for this session
      const newMessage: Message = {
        id: `msg-${Date.now()}-${++messageIdCounter}-${sessionId}`,
        role: 'assistant',
        content: event.content || '',
        createdAt: Date.now(),
        sessionId: sessionId,
      };
      streamingMessages.set(sessionId, newMessage);
      useChatStore.getState().addMessage(newMessage);
    } else if (currentMessage.role === 'assistant') {
      // Append to existing message
      currentMessage.content = (currentMessage.content || '') + event.content;
      const updatedContent = currentMessage.content;
      useChatStore
        .getState()
        .updateMessage(currentMessage.id, { content: updatedContent });
    }
  });

  // Response handler - finalize message per session
  eventBus.on('response', (event) => {
    if (event.type !== 'response') return;
    const sessionId = event.sessionId || 'default';
    const currentMessage = streamingMessages.get(sessionId);

    if (currentMessage && currentMessage.role === 'assistant') {
      // Update existing streaming message
      useChatStore.getState().updateMessage(currentMessage.id, {
        content: event.text || currentMessage.content,
        tokenUsage: event.tokenUsage,
        model: event.model,
        provider: event.provider,
        router: event.router,
        reasoning: event.reasoning,
      });
    } else if (event.text) {
      // No streaming message - create complete message (historical replay)
      // Use counter in ID to prevent collision when multiple messages arrive in same millisecond
      const assistantMessage: Message = {
        id: `msg-${Date.now()}-${++messageIdCounter}-${sessionId}`,
        role: 'assistant',
        content: event.text,
        createdAt: Date.now(),
        sessionId: sessionId,
        tokenUsage: event.tokenUsage,
        model: event.model,
        provider: event.provider,
        router: event.router,
        reasoning: event.reasoning,
      };
      useChatStore.getState().addMessage(assistantMessage);
    }

    // Clean up streaming message for this session
    streamingMessages.delete(sessionId);
    useChatStore.getState().setProcessing(sessionId, false);
    useAgentStore.getState().setAgentStatus('idle');

    // Generate session title if this is the first response and we haven't generated one yet
    if (sessionId && sessionId !== 'default' && !titlesGenerated.has(sessionId) && apiClientRef) {
      titlesGenerated.add(sessionId);

      // Generate title asynchronously (don't block the response handler)
      apiClientRef.generateSessionTitle(sessionId)
        .then(({ title }) => {
          if (title) {
            console.log('[EventHandlers] Generated session title:', { sessionId, title });

            // Manually update stores since backend doesn't emit sessionTitleUpdated event
            useSessionStore.getState().notifySessionEvent({
              type: 'title_updated',
              sessionId: sessionId,
              title: title,
            });

            // Also update the tab title if a tab exists for this session
            const tabId = `session-${sessionId}`;
            const tab = useChatTabsStore.getState().getTab(tabId);
            console.log('[EventHandlers] Looking for tab after title generation:', { tabId, found: !!tab });
            if (tab) {
              console.log('[EventHandlers] Updating tab title after generation:', { tabId, title });
              useChatTabsStore.getState().updateTabTitle(tabId, title);
            }
          }
        })
        .catch((error) => {
          console.warn('[EventHandlers] Failed to generate session title:', error);
          // Remove from tracking so we can retry on next message if needed
          titlesGenerated.delete(sessionId);
        });
    }
  });

  // Tool call handler
  eventBus.on('toolCall', (event) => {
    if (event.type !== 'toolCall') return;
    const sessionId = event.sessionId || useSessionStore.getState().currentSessionId;

    console.log('[Handler] toolCall event:', {
      toolCallId: event.toolCallId,
      toolName: event.toolName,
      sessionId,
      messageId: `tool-${event.toolCallId}`
    });

    useAgentStore.getState().setAgentStatus('executing_tool', `Using tool: ${event.toolName}`);

    // Check if there's already an approval message for this tool call
    // We need to check by ID (approval-${toolCallId}) to handle cases where
    // the toolResult event arrives before the toolCall event
    const messages = useChatStore.getState().getMessages(sessionId || 'default');
    const approvalMessageId = `approval-${event.toolCallId}`;
    const existingApprovalMessage = messages.find(m => m.id === approvalMessageId);

    if (existingApprovalMessage) {
      // Approval message already exists - skip creating duplicate tool message
      // The approval message serves as the tool message and will be updated by toolResult
      console.log('[Handler] Found existing approval message:', approvalMessageId, '- skipping duplicate tool message creation');
      return;
    }

    // Also check for any approval message with matching toolName that doesn't have a result yet
    // This handles cases where approvalId != toolCallId
    const approvalMessages = messages.filter(m =>
      m.role === 'tool' &&
      m.id.startsWith('approval-') &&
      m.toolResult === undefined
    );

    console.log('[Handler] Checking for pending approval messages:', {
      incomingToolName: event.toolName,
      approvalMessages: approvalMessages.map(m => ({ id: m.id, toolName: (m as any).toolName }))
    });

    const pendingApprovalMessage = approvalMessages.find(m => (m as any).toolName === event.toolName);

    if (pendingApprovalMessage) {
      console.log('[Handler] Found pending approval message by toolName:', pendingApprovalMessage.id, '- skipping duplicate tool message creation');
      return;
    }

    // Add tool call message (only if no approval message exists)
    const toolMessage: Message = {
      id: `tool-${event.toolCallId}`,
      role: 'tool',
      content: null,
      toolName: event.toolName,
      toolArgs: event.toolArgs,
      createdAt: Date.now(),
      sessionId: sessionId || undefined,
      // During history replay, if backend sent approval metadata, preserve it
      ...((event as any).requireApproval && { requireApproval: true }),
      ...((event as any).approvalStatus && { approvalStatus: (event as any).approvalStatus }),
    };
    useChatStore.getState().addMessage(toolMessage);
  });

  // Tool result handler
  eventBus.on('toolResult', (event) => {
    if (event.type !== 'toolResult') return;
    console.log('[Handler] toolResult event:', {
      toolCallId: event.toolCallId,
      toolName: event.toolName,
      success: event.success,
      sessionId: event.sessionId,
      resultType: typeof event.result,
      resultIsUndefined: event.result === undefined,
      resultPreview: typeof event.result === 'string' ? event.result.substring(0, 100) : typeof event.result,
      hasResultKey: 'result' in event
    });

    // Get current session to find the right message
    const sessionId = event.sessionId || useSessionStore.getState().currentSessionId || 'default';
    const messages = useChatStore.getState().getMessages(sessionId);
    const toolCallId = event.toolCallId;
    const result = event.result;
    const success = event.success;
    const toolName = event.toolName;

    // Handle scheduler tool results - update scheduler store
    if (toolName && success) {
      console.log('[Handler] 📅 About to call handleSchedulerToolResult:', {
        toolName,
        resultType: typeof result,
        result: result,
      });
      handleSchedulerToolResult(toolName, result);
    }

    // Try to find the tool message - it could be either:
    // 1. Regular tool call: `tool-${toolCallId}`
    // 2. Auto-approved tool: `approval-${approvalId}` (where approvalId might equal toolCallId)
    // 3. If IDs don't match, find by looking for a tool message without a result

    const toolMessageId = `tool-${toolCallId}`;
    const approvalMessageId = `approval-${toolCallId}`;

    // Check if either of these IDs exist
    const existingById = messages.find(m => m.id === toolMessageId || m.id === approvalMessageId);

    if (existingById) {
      // Found by ID - update it
      console.log('[Handler] Found tool message by ID:', existingById.id);
      useChatStore.getState().updateMessage(existingById.id, {
        toolResult: result,
        toolResultSuccess: success,
      });
    } else {
      // ID didn't match - find the most recent tool message without a result
      // This handles cases where toolCallId != approvalId
      // IMPORTANT: Prioritize approval messages over regular tool messages
      const pendingMessages = messages
        .filter(m => m.role === 'tool' && m.toolResult === undefined)
        .sort((a, b) => b.createdAt - a.createdAt);

      // First try to find an approval message
      const pendingApprovalMessage = pendingMessages.find(m => m.id.startsWith('approval-'));
      const pendingToolMessage = pendingApprovalMessage || pendingMessages[0];

      if (pendingToolMessage) {
        console.log('[Handler] Found pending tool message by search:', pendingToolMessage.id, pendingToolMessage.role === 'tool' ? (pendingToolMessage as any).toolName : 'N/A');
        if (pendingToolMessage.role === 'tool') {
          useChatStore.getState().updateMessage(pendingToolMessage.id, {
            toolResult: result,
            toolResultSuccess: success,
          });
        }
      } else {
        console.warn('[Handler] Could not find tool message to update for toolCallId:', toolCallId);
      }
    }
  });

  // Error handler
  eventBus.on('error', (event) => {
    if (event.type !== 'error') return;
    const sessionId = event.sessionId || 'default';

    useChatStore.getState().setError(sessionId, {
      type: 'error',
      message: event.error,
      sessionId: sessionId,
    });
    useChatStore.getState().setProcessing(sessionId, false);
    streamingMessages.delete(sessionId);
    useAgentStore.getState().setAgentStatus('error', event.error);
  });

  // Conversation reset handler
  eventBus.on('conversationReset', (event) => {
    if (event.type !== 'conversationReset') return;
    const sessionId = event.sessionId || 'default';
    useChatStore.getState().clearMessages(sessionId);
    useChatStore.getState().setProcessing(sessionId, false);
    streamingMessages.delete(sessionId);
    useAgentStore.getState().setAgentStatus('idle');
  });

  // Unified approval request handler (tool confirmation, command confirmation, elicitation)
  eventBus.on('approvalRequest', (event: ApprovalRequestEvent) => {
    console.log('[Handlers] Received approvalRequest event:', {
      approvalId: event.approvalId,
      approvalType: event.approvalType,
      sessionId: event.sessionId,
      hasMetadata: !!event.metadata,
    });

    const sessionId = event.sessionId;
    if (!sessionId) {
      console.warn('[Handlers] approvalRequest missing sessionId, skipping');
      return;
    }

    if (isToolConfirmationApproval(event)) {
      console.log('[Handlers] Processing tool_confirmation approval:', {
        approvalId: event.approvalId,
        toolName: event.toolName,
        messageId: `approval-${event.approvalId}`,
        autoApproved: event.metadata?.autoApproved
      });

      // Tool confirmation approval
      useAgentStore
        .getState()
        .setAgentStatus('awaiting_approval', event.description || `Approve ${event.toolName}`);

      useApprovalStore.getState().addApproval({
        approvalId: event.approvalId,
        toolName: event.toolName,
        toolArgs: event.args,
        message: event.description,
        sessionId: sessionId,
      });

      const approvalMessage: Message = {
        id: `approval-${event.approvalId}`,
        role: 'tool',
        content: null,
        toolName: event.toolName,
        toolArgs: event.args,
        createdAt: Date.now(),
        sessionId: sessionId,
        requireApproval: true, // Mark this tool as requiring approval
        approvalStatus: 'pending', // Initial status
      };
      useChatStore.getState().addMessage(approvalMessage);
    } else if (isCommandConfirmationApproval(event)) {
      // Command confirmation approval
      useAgentStore
        .getState()
        .setAgentStatus('awaiting_approval', `Approve command in ${event.toolName}`);

      useApprovalStore.getState().addApproval({
        approvalId: event.approvalId,
        toolName: event.toolName,
        toolArgs: { command: event.command },
        message: `Command: ${event.command}`,
        sessionId: sessionId,
      });

      const approvalMessage: Message = {
        id: `approval-${event.approvalId}`,
        role: 'tool',
        content: null,
        toolName: event.toolName,
        toolArgs: { command: event.command },
        createdAt: Date.now(),
        sessionId: sessionId,
        requireApproval: true, // Mark this tool as requiring approval
        approvalStatus: 'pending', // Initial status
      };
      useChatStore.getState().addMessage(approvalMessage);
    } else if (isElicitationApproval(event)) {
      // Elicitation (ask_user) approval
      const elicitationMeta = getElicitationMetadata(event);
      if (!elicitationMeta) {
        console.error('[Handlers] Invalid elicitation metadata');
        return;
      }

      useAgentStore
        .getState()
        .setAgentStatus('awaiting_input', elicitationMeta.prompt);

      // Store as approval for unified handling
      useApprovalStore.getState().addApproval({
        approvalId: event.approvalId,
        toolName: 'ask_user',
        toolArgs: { schema: elicitationMeta.schema },
        message: elicitationMeta.prompt,
        sessionId: sessionId,
        metadata: event.metadata, // Store full metadata for form rendering
      });

      // Add inline message for elicitation
      const elicitationMessage: Message = {
        id: `elicitation-${event.approvalId}`,
        role: 'tool',
        content: null,
        toolName: 'ask_user',
        toolArgs: { prompt: elicitationMeta.prompt },
        createdAt: Date.now(),
        sessionId: sessionId,
        elicitation: elicitationMeta, // Add elicitation data to message
      };
      useChatStore.getState().addMessage(elicitationMessage);
    }
  });

  // Approval resolved handler (server confirmation)
  eventBus.on('approvalResolved', (event: ApprovalResolvedEvent) => {
    if (event.type !== 'approvalResolved') return;
    const { approvalId, status, sessionId } = event;

    // Map server status to store status
    const storeStatus = status === 'approved' ? 'approved' :
                        status === 'denied' ? 'rejected' : 'rejected';

    // Update approval store (handles server-side timeouts/cancellations)
    useApprovalStore.getState().updateApprovalStatus(approvalId, storeStatus);

    // Update the message approval status for audit trail
    const messageId = `approval-${approvalId}`;
    const messages = useChatStore.getState().getMessages(sessionId || 'default');
    const approvalMessage = messages.find(m => m.id === messageId);

    if (approvalMessage && approvalMessage.role === 'tool') {
      console.log('[Handlers] Updating approval status for message:', messageId, 'to', storeStatus);
      useChatStore.getState().updateMessage(messageId, {
        approvalStatus: storeStatus,
      });
    }

    // Update agent status based on result
    if (status === 'approved') {
      useAgentStore.getState().setAgentStatus('thinking');
    } else {
      useAgentStore.getState().setAgentStatus('idle');
    }
  });

  // Task handlers
  eventBus.on('taskCreated', (event) => {
    if (event.type !== 'taskCreated') return;
    useTasksStore.getState().addTask(event.task);
  });

  eventBus.on('taskUpdated', (event) => {
    if (event.type !== 'taskUpdated') return;
    useTasksStore.getState().updateTask(event.task.taskId, event.task);

    // Update agent status if task completed/failed
    if (['completed', 'failed'].includes(event.task.status)) {
      useAgentStore.getState().setAgentStatus('idle');
    }
  });

  eventBus.on('taskDeleted', (event) => {
    if (event.type !== 'taskDeleted') return;
    useTasksStore.getState().removeTask(event.taskId);
  });

  // Scheduler handlers (SSE events from /api/events stream)
  // Note: These may fire alongside tool result handlers - the store handles deduplication
  eventBus.on('scheduler:scheduleCreated', (event: any) => {
    console.log('[EventHandlers] 📅 Schedule created event:', event);
    useSchedulerStore.getState().addSchedule(event.schedule);
    console.log('[EventHandlers] 🏪 Store after add:', Array.from(useSchedulerStore.getState().schedules.values()));

    // Toast and activity log (SSE event source - may duplicate with tool handler, but that's ok for UX)
    useNotificationStore.getState().addToast({
      title: 'Routine created',
      description: `"${event.schedule.name}" has been scheduled`,
      intent: 'success',
    });

    useEventLogStore.getState().addEvent({
      type: 'success',
      category: 'scheduler',
      title: 'Schedule created',
      description: event.schedule.name,
      metadata: { scheduleId: event.schedule.id, cron: event.schedule.cronExpression },
    });
  });

  eventBus.on('scheduler:scheduleUpdated', (event: any) => {
    console.log('[EventHandlers] 📝 Schedule updated event:', event);
    useSchedulerStore.getState().updateSchedule(event.schedule.id, event.schedule);

    useNotificationStore.getState().addToast({
      title: 'Routine updated',
      description: `"${event.schedule.name}" has been modified`,
      intent: 'info',
    });

    useEventLogStore.getState().addEvent({
      type: 'info',
      category: 'scheduler',
      title: 'Schedule updated',
      description: event.schedule.name,
      metadata: { scheduleId: event.schedule.id },
    });
  });

  eventBus.on('scheduler:scheduleDeleted', (event: any) => {
    console.log('[EventHandlers] 🗑️ Schedule deleted event:', event);
    // Get name before deletion
    const existingSchedule = useSchedulerStore.getState().schedules.get(event.scheduleId);
    const scheduleName = existingSchedule?.name || 'Schedule';

    useSchedulerStore.getState().removeSchedule(event.scheduleId);

    useNotificationStore.getState().addToast({
      title: 'Routine deleted',
      description: `"${scheduleName}" has been removed`,
      intent: 'info',
    });

    useEventLogStore.getState().addEvent({
      type: 'info',
      category: 'scheduler',
      title: 'Schedule deleted',
      description: scheduleName,
      metadata: { scheduleId: event.scheduleId },
    });
  });

  eventBus.on('scheduler:scheduleTriggered', (event: any) => {
    console.log('[EventHandlers] 🚀 Schedule triggered:', {
      scheduleId: event.schedule.id,
      scheduleName: event.schedule.name,
      sessionId: event.sessionId,
      executionSessionId: event.executionSessionId,
    });

    useSchedulerStore.getState().setScheduleRunning(event.schedule.id, true);
    useSchedulerStore.getState().updateSchedule(event.schedule.id, {
      lastRunAt: event.triggeredAt,
    });

    // Show toast notification
    useNotificationStore.getState().addToast({
      title: 'Routine started',
      description: `"${event.schedule.name}" is now running`,
      intent: 'info',
    });

    // Add to activity log
    useEventLogStore.getState().addEvent({
      type: 'info',
      category: 'scheduler',
      title: 'Schedule triggered',
      description: `${event.schedule.name} started`,
      metadata: { scheduleId: event.schedule.id, sessionId: event.executionSessionId || event.sessionId },
    });

    // Auto-open a tab for the schedule execution session
    const executionSessionId = event.executionSessionId || event.sessionId;
    if (executionSessionId) {
      const tabId = `session-${executionSessionId}`;
      const existingTab = useChatTabsStore.getState().getTab(tabId);

      if (existingTab) {
        // Tab exists - update its status to running
        useChatTabsStore.getState().updateTabStatus(tabId, 'running');
      } else {
        // Create new tab with running status
        useChatTabsStore.getState().addTab({
          id: tabId,
          title: `${event.schedule.name}`,
          sessionId: executionSessionId,
          isPinned: false,
          status: 'running',
          scheduleId: event.schedule.id,
        });
      }
    }
  });

  eventBus.on('scheduler:executionCompleted', (event: any) => {
    console.log('[EventHandlers] ✅ Schedule completed:', {
      scheduleId: event.schedule.id,
      scheduleName: event.schedule.name,
      sessionId: event.log?.sessionId,
    });

    const store = useSchedulerStore.getState();
    store.setScheduleRunning(event.schedule.id, false);
    store.addExecutionLog(event.log);
    store.updateSchedule(event.schedule.id, {
      runCount: event.schedule.runCount,
      successCount: event.schedule.successCount,
      nextRunAt: event.schedule.nextRunAt,
      lastError: undefined,
    });

    // Show toast notification
    useNotificationStore.getState().addToast({
      title: 'Routine completed',
      description: `"${event.schedule.name}" finished successfully`,
      intent: 'success',
    });

    // Add to activity log
    useEventLogStore.getState().addEvent({
      type: 'success',
      category: 'scheduler',
      title: 'Schedule completed',
      description: `${event.schedule.name} finished`,
      metadata: { scheduleId: event.schedule.id, sessionId: event.log?.sessionId },
    });

    // Update tab status to completed
    const sessionId = event.log?.sessionId || event.sessionId;
    if (sessionId) {
      const tabId = `session-${sessionId}`;
      const existingTab = useChatTabsStore.getState().getTab(tabId);
      if (existingTab) {
        useChatTabsStore.getState().updateTabStatus(tabId, 'completed');
      }
    }
  });

  eventBus.on('scheduler:executionFailed', (event: any) => {
    console.log('[EventHandlers] ❌ Schedule failed:', {
      scheduleId: event.schedule.id,
      scheduleName: event.schedule.name,
      error: event.error,
      sessionId: event.log?.sessionId,
    });

    const store = useSchedulerStore.getState();
    store.setScheduleRunning(event.schedule.id, false);
    store.addExecutionLog(event.log);
    store.updateSchedule(event.schedule.id, {
      runCount: event.schedule.runCount,
      failureCount: event.schedule.failureCount,
      nextRunAt: event.schedule.nextRunAt,
      lastError: event.error,
    });

    // Show toast notification
    useNotificationStore.getState().addToast({
      title: 'Routine failed',
      description: `"${event.schedule.name}" encountered an error`,
      intent: 'danger',
    });

    // Add to activity log
    useEventLogStore.getState().addEvent({
      type: 'error',
      category: 'scheduler',
      title: 'Schedule failed',
      description: `${event.schedule.name}: ${event.error || 'Unknown error'}`,
      metadata: { scheduleId: event.schedule.id, sessionId: event.log?.sessionId, error: event.error },
    });

    // Update tab status to failed
    const sessionId = event.log?.sessionId || event.sessionId;
    if (sessionId) {
      const tabId = `session-${sessionId}`;
      const existingTab = useChatTabsStore.getState().getTab(tabId);
      if (existingTab) {
        useChatTabsStore.getState().updateTabStatus(tabId, 'failed');
      }
    }
  });

  // Session title updated handler
  eventBus.on('sessionTitleUpdated', (event) => {
    if (event.type !== 'sessionTitleUpdated') return;
    if (event.sessionId && event.title) {
      console.log('[EventHandlers] Session title updated:', {
        sessionId: event.sessionId,
        title: event.title
      });

      useSessionStore.getState().notifySessionEvent({
        type: 'title_updated',
        sessionId: event.sessionId,
        title: event.title,
      });

      // Also update the tab title if a tab exists for this session
      const tabId = `session-${event.sessionId}`;
      const tab = useChatTabsStore.getState().getTab(tabId);
      console.log('[EventHandlers] Looking for tab:', { tabId, found: !!tab });
      if (tab) {
        console.log('[EventHandlers] Updating tab title:', { tabId, title: event.title });
        useChatTabsStore.getState().updateTabTitle(tabId, event.title);
        console.log('[EventHandlers] Tab updated, new tabs:', useChatTabsStore.getState().tabs);
      }
    }
  });

  // UI Action handler - allows agent to control UI elements
  // The backend sends args, but we also check payload for backwards compatibility
  eventBus.on('uiAction', (event) => {
    if (event.type !== 'uiAction') return;
    const args = event.args || event.payload || {};
    console.log('[Handler] uiAction event:', { action: event.action, args });

    const artifactStore = useArtifactPanelStore.getState();

    switch (event.action) {
      case 'ui_open_browser':
        // Handle both url in args and empty URL case
        artifactStore.openBrowser(args.url as string || '', args.title as string);
        break;
      case 'ui_open_notes':
        artifactStore.openNotesList();
        break;
      case 'ui_create_note':
        console.log('[Handler] Creating note with:', { title: args.title, content: args.content });
        // Create actual note in localStorage
        const newNoteFromUI = createNoteInStorage(args.title as string, args.content as string);

        // Register as artifact and open
        const artifactIdFromUI = `note-${newNoteFromUI.id}`;
        artifactStore.registerArtifact({
          id: artifactIdFromUI,
          type: 'note',
          title: newNoteFromUI.title,
          isPinned: false,
          lastOpened: Date.now(),
          createdAt: newNoteFromUI.createdAt,
          modifiedAt: newNoteFromUI.updatedAt,
          data: { noteId: newNoteFromUI.id },
        });

        artifactStore.openTab({
          id: artifactIdFromUI,
          type: 'note',
          title: newNoteFromUI.title,
          data: { noteId: newNoteFromUI.id },
        });
        break;
      case 'ui_close_panel':
        artifactStore.closePanel();
        break;
      // Legacy action names for backwards compatibility
      case 'openBrowser':
        artifactStore.openBrowser(args.url as string || '', args.title as string);
        break;
      case 'openNote':
        if (args.noteId) {
          artifactStore.openNote(args.noteId as string, (args.title as string) || 'Note');
        }
        break;
      case 'openNotesList':
        artifactStore.openNotesList();
        break;
      case 'createNote':
        // Create actual note in localStorage
        const newNote = createNoteInStorage(args.title as string, args.content as string);

        // Register as artifact and open
        const artifactId = `note-${newNote.id}`;
        artifactStore.registerArtifact({
          id: artifactId,
          type: 'note',
          title: newNote.title,
          isPinned: false,
          lastOpened: Date.now(),
          createdAt: newNote.createdAt,
          modifiedAt: newNote.updatedAt,
          data: { noteId: newNote.id },
        });

        artifactStore.openTab({
          id: artifactId,
          type: 'note',
          title: newNote.title,
          data: { noteId: newNote.id },
        });
        break;
      case 'closeArtifactPanel':
        artifactStore.closePanel();
        break;
      default:
        console.warn('[Handler] Unknown uiAction:', event.action);
    }
  });

  // DISABLED: Trigger system event handlers (webhooks, A2A callbacks, async operations)
  // These require trigger events not available on multi-agent-server
  /*
  eventBus.on('trigger:created', (event) => {
    if (event.type !== 'trigger:created') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:created event:', { taskId: event.taskId, sessionId, status: event.status });
  });

  eventBus.on('trigger:processing', (event) => {
    if (event.type !== 'trigger:processing') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:processing event:', { taskId: event.taskId, sessionId });
    useChatStore.getState().setProcessing(sessionId, true);
    useAgentStore.getState().setAgentStatus('thinking', 'Processing async operation...');
  });

  eventBus.on('trigger:completed', (event) => {
    if (event.type !== 'trigger:completed') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:completed event:', { taskId: event.taskId, sessionId, response: event.response });
    if (event.response?.message) {
      const message: Message = {
        id: `msg-${Date.now()}-${++messageIdCounter}-${sessionId}`,
        role: 'assistant',
        content: event.response.message,
        createdAt: Date.now(),
        sessionId,
      };
      useChatStore.getState().addMessage(message);
    }
    useChatStore.getState().setProcessing(sessionId, false);
    useAgentStore.getState().setAgentStatus('idle', '');
  });

  eventBus.on('trigger:failed', (event) => {
    if (event.type !== 'trigger:failed') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:failed event:', { taskId: event.taskId, sessionId, error: event.error });
    useChatStore.getState().setError(sessionId, event.error);
    useChatStore.getState().setProcessing(sessionId, false);
    useAgentStore.getState().setAgentStatus('idle', '');
  });

  eventBus.on('trigger:pending', (event) => {
    if (event.type !== 'trigger:pending') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:pending event:', { taskId: event.taskId, sessionId, reason: event.reason });
  });

  eventBus.on('trigger:approved', (event) => {
    if (event.type !== 'trigger:approved') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:approved event:', { taskId: event.taskId, sessionId });
  });

  eventBus.on('trigger:rejected', (event) => {
    if (event.type !== 'trigger:rejected') return;
    const sessionId = event.sessionId || 'default';
    console.log('[Handler] trigger:rejected event:', { taskId: event.taskId, sessionId, reason: event.reason });
  });
  */
}

/**
 * Reset handler initialization state (called by event bus reset)
 */
export function resetEventHandlers(): void {
  console.log('[EventHandlers] Resetting handler initialization state');
  handlersInitialized = false;
  titlesGenerated.clear();
  streamingMessages.clear();
  apiClientRef = null;
  messageIdCounter = 0;
}

/**
 * Cleanup session-specific state (called when a session is deleted)
 */
export function cleanupSession(sessionId: string): void {
  streamingMessages.delete(sessionId);
  titlesGenerated.delete(sessionId);
}

/**
 * Rehydrate scheduler state from backend
 * Called on app startup/reconnection to populate existing schedules
 */
export async function rehydrateSchedules(apiClient: { listSchedules: () => Promise<any[]> }): Promise<void> {
  try {
    console.log('[EventHandlers] 🔄 Starting schedule rehydration...');

    // Set loading state
    useSchedulerStore.getState().setLoading(true);

    const schedules = await apiClient.listSchedules();
    console.log(`[EventHandlers] ✅ Found ${schedules.length} schedule(s):`, schedules);

    // Set all schedules at once
    useSchedulerStore.getState().setSchedules(schedules);

    const storeSchedules = Array.from(useSchedulerStore.getState().schedules.values());
    console.log('[EventHandlers] 🏪 Store now contains', storeSchedules.length, 'schedule(s)');
  } catch (error) {
    console.error('[EventHandlers] 💥 Failed to rehydrate schedules:', error);
    useSchedulerStore.getState().setError('Failed to load schedules');
    // Don't throw - rehydration failure shouldn't break the app
  } finally {
    useSchedulerStore.getState().setLoading(false);
  }
}
